// @spec RSK-STR-003
'use client';

import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { RiskForm } from '@/features/risks/components/RiskForm';
import { useCreateRisk } from '@/features/risks/hooks/useRisks';
import { riskFormDefaults, type RiskFormData } from '@/features/risks/schemas/risk-form';
import i18n from '@/lib/i18n';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/_authenticated/risks/new')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.createRisk'),
  }),
  component: CreateRiskPage,
});

/**
 * Create Risk page - Form for manually creating new risks.
 *
 * Features:
 * - Full-featured risk creation form
 * - Live risk score calculation
 * - Risk owner selection
 * - All new risks start in 'draft' status
 */
function CreateRiskPage() {
  const { t } = useTranslation(['risks', 'common']);
  const navigate = useNavigate();
  const { mutateAsync, isPending } = useCreateRisk();

  const handleSubmit = async (data: RiskFormData) => {
    try {
      const result = await mutateAsync({
        title: data.title,
        description: data.description,
        category: data.category,
        subcategory: data.subcategory ?? null,
        inherentLikelihood: data.inherentLikelihood,
        inherentImpact: data.inherentImpact,
        likelihoodRationale: data.likelihoodRationale ?? null,
        impactRationale: data.impactRationale ?? null,
        ownerId: data.ownerId,
        riskSource: data.riskSource ?? null,
        sourceReference: data.sourceReference ?? null,
        linkedAssetIds: data.linkedAssetIds,
        tags: data.tags,
      });

      toast.success(t('risks:toast.riskCreatedSuccessfully'), {
        description: t('risks:createPage.successDesc', { riskId: result.riskId }),
      });

      // Navigate to risk register or risk detail page once available
      // For now, go back to a placeholder
      void navigate({ to: '/' });
    } catch (error) {
      const message = error instanceof Error ? error.message : t('risks:createPage.unexpectedError');
      toast.error(t('risks:toast.failedToCreateRisk'), {
        description: message,
      });
    }
  };

  const handleCancel = () => {
    void navigate({ to: '/' });
  };

  return (
    <div className="container max-w-4xl py-6">
      {/* Header */}
      <div className="mb-6">
        <div className="mb-4">
          <Button variant="ghost" size="sm" asChild className="-ml-2">
            <Link to="/">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Link>
          </Button>
        </div>
        <h1 className="text-2xl font-semibold tracking-tight">{t('common:routeErrors.createRisk')}</h1>
        <p className="text-muted-foreground">
          Add a new risk to your risk register. It will be saved as a draft until ready for review.
        </p>
      </div>

      {/* Form */}
      <div className="rounded-lg border bg-card p-6">
        <RiskForm
          defaultValues={riskFormDefaults}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isSubmitting={isPending}
        />
      </div>
    </div>
  );
}
